import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUpload } from "@/components/ui/file-upload";
import { FileImage } from "@/components/ui/file-image";
import { useBranding } from "@/contexts/BrandingContext";
import { UpgradeBanner } from "@/components/PlanGating";
import { usePlanFlags } from "@/contexts/PlanFlagsContext";
import EmployeeNumberConfigPanel from "@/components/settings/EmployeeNumberConfigPanel";
import StudentNumberConfigPanel from "@/components/settings/StudentNumberConfigPanel";

export default function BrandingTab() {
  const { refresh } = useBranding();
  const { flags } = usePlanFlags();
  const canEdit = flags?.has_custom_branding !== false;
  const [form, setForm] = useState({
    brand_name: "",
    logo_url: "",
    logo_file_id: "",
    primary_color: "#4A6753",
    login_tagline: "",
    footer_text: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  // "upload" | "url" — controls which input is enabled. Uploaded logo wins
  // if both accidentally exist (backend enforces the same).
  const [logoMode, setLogoMode] = useState("upload");

  const load = async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setForm({ ...data, logo_file_id: data.logo_file_id || "" });
      setLogoMode((data.logo_file_id || "").length > 0 ? "upload"
                    : ((data.logo_url || "").length > 0 ? "url" : "upload"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickUpload = () => {
    // Switching to Upload mode clears any typed URL so the two methods
    // remain mutually exclusive in the form state too.
    setLogoMode("upload");
    if (form.logo_url) set("logo_url", "");
  };
  const pickUrl = () => {
    setLogoMode("url");
    if (form.logo_file_id) set("logo_file_id", "");
  };

  const save = async () => {
    if (!form.brand_name.trim()) { toast.error("Brand name is required"); return; }
    if (!/^#[0-9a-fA-F]{6}$/.test(form.primary_color)) {
      toast.error("Primary color must be a 6-digit hex like #4A6753");
      return;
    }
    setSaving(true);
    try {
      // Enforce mutual exclusion on submit: uploaded file wins over URL.
      const payload = { ...form };
      if ((form.logo_file_id || "").length > 0) payload.logo_url = "";
      await api.put("/settings/branding", payload);
      toast.success("Branding saved — reload the page to see it everywhere");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const preview = form.brand_name || "TherapyFlow";

  return (
    <div className="space-y-4">
      <UpgradeBanner flagKey="has_custom_branding" />
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Brand identity</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Customize how the app looks for your organization. Changes apply to the sidebar, login page, browser tab title and PDF invoices.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-1 sm:col-span-2">
              <Label>Brand / organization name</Label>
              <Input
                value={form.brand_name}
                onChange={e => set("brand_name", e.target.value)}
                placeholder="TherapyFlow"
                data-testid="brand-name"
                disabled={!loaded}
                maxLength={60}
              />
              <div className="text-[11px] text-muted-foreground">Shown in the sidebar, login page and browser tab title.</div>
            </div>

            {/* Logo — mutually exclusive Upload vs URL */}
            <div className="space-y-2 col-span-1 sm:col-span-2 border rounded-md p-3 bg-muted/20" data-testid="brand-logo-section">
              <div className="flex items-center gap-2">
                <Label className="mb-0">Logo</Label>
                <div className="inline-flex rounded-md border bg-card p-0.5 text-xs" data-testid="brand-logo-mode-toggle">
                  <button
                    type="button"
                    onClick={pickUpload}
                    className={`px-2.5 py-1 rounded ${logoMode === "upload" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="brand-logo-mode-upload"
                  >Upload Image</button>
                  <button
                    type="button"
                    onClick={pickUrl}
                    className={`px-2.5 py-1 rounded ${logoMode === "url" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="brand-logo-mode-url"
                  >Use URL</button>
                </div>
                <span className="text-[11px] text-muted-foreground ml-auto">
                  Choose one — an uploaded image always wins over a URL.
                </span>
              </div>

              {logoMode === "upload" ? (
                <div className="flex items-start gap-3" data-testid="brand-logo-upload-panel">
                  <FileUpload
                    value={form.logo_file_id || null}
                    onChange={(id) => { set("logo_file_id", id || ""); if (id) set("logo_url", ""); }}
                    entityType="branding"
                    entityId="logo"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    size={96}
                  />
                  <div className="text-[11px] text-muted-foreground pt-2">
                    Square PNG / SVG works best (recommend 128×128 or larger). We preserve aspect ratio everywhere the logo is rendered.
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="brand-logo-url-panel">
                  <Input
                    value={form.logo_url}
                    onChange={e => set("logo_url", e.target.value)}
                    placeholder="https://…/logo.png"
                    data-testid="brand-logo-url"
                    disabled={!loaded}
                  />
                  <div className="text-[11px] text-muted-foreground">Must be a public HTTPS URL to a PNG / SVG / JPEG.</div>
                  {form.logo_url && (
                    <div className="border rounded-md bg-card p-2 inline-block">
                      <img
                        src={form.logo_url}
                        alt="Logo preview"
                        style={{ maxHeight: 64, maxWidth: 160, height: "auto", width: "auto", objectFit: "contain" }}
                        data-testid="brand-logo-url-preview"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Primary brand color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={form.primary_color}
                  onChange={e => set("primary_color", e.target.value.toUpperCase())}
                  className="h-10 w-16 p-1 cursor-pointer"
                  data-testid="brand-color-picker"
                  disabled={!loaded}
                />
                <Input
                  value={form.primary_color}
                  onChange={e => set("primary_color", e.target.value.toUpperCase())}
                  placeholder="#4A6753"
                  className="font-mono"
                  data-testid="brand-color-hex"
                  disabled={!loaded}
                  maxLength={7}
                />
              </div>
              <div className="text-[11px] text-muted-foreground">Used for buttons, links, and highlighted UI.</div>
            </div>

            <div className="space-y-1.5">
              <Label>Preview</Label>
              <div className="flex items-center gap-3">
                {form.logo_file_id ? (
                  <FileImage fileId={form.logo_file_id} alt="Logo" style={{ maxHeight: 40, maxWidth: 80, objectFit: "contain" }} />
                ) : form.logo_url ? (
                  <img src={form.logo_url} alt="Logo" style={{ maxHeight: 40, maxWidth: 80, objectFit: "contain" }} />
                ) : null}
                <div
                  className="h-10 flex-1 rounded-md flex items-center justify-center text-white font-medium text-sm shadow-sm"
                  style={{ backgroundColor: form.primary_color }}
                  data-testid="brand-preview-swatch"
                >
                  {preview}
                </div>
              </div>
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label>Login page tagline</Label>
              <Input
                value={form.login_tagline}
                onChange={e => set("login_tagline", e.target.value)}
                placeholder="Run your therapy center without paper."
                data-testid="brand-tagline"
                disabled={!loaded}
                maxLength={200}
              />
              <div className="text-[11px] text-muted-foreground">The big headline on the login hero panel.</div>
            </div>

            <div className="space-y-1.5 col-span-1 sm:col-span-2">
              <Label>Login page footer text</Label>
              <Input
                value={form.footer_text}
                onChange={e => set("footer_text", e.target.value)}
                placeholder="Built for real therapy businesses."
                data-testid="brand-footer"
                disabled={!loaded}
                maxLength={200}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving || !loaded || !canEdit}
          data-testid="save-branding"
          size="lg"
        >
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>

      {/* Iter-46 — Employee Number Configuration */}
      <EmployeeNumberConfigPanel />

      {/* Iter-67 — Student ID Configuration */}
      <StudentNumberConfigPanel />
    </div>
  );
}
